import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Grid,
  Card,
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Tabs,
  Tab,
  Divider,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Alert,
  TablePagination,
  LinearProgress,
  Collapse,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Send as SendIcon,
  Refresh as RefreshIcon,
  History as HistoryIcon,
  Message as MessageIcon,
  Article as ArticleIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  PersonAdd as PersonAddIcon,
  PhoneAndroid as PhoneIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  HourglassEmpty as PendingIcon,
  Visibility as VisibilityIcon,
  Info as InfoIcon,
  Sms as SmsIcon,
  Inbox as InboxIcon,
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import LoaderOverlay from 'components/LoaderOverlay';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import { api } from '../../api';

// ─── theme ──────────────────────────────────────────────────────────────────
// Brand teal used throughout the app's tables (opdAppointments, pharmacy
// billing/Journal & Ledger) — gradient header row, zebra body rows, soft
// hover tint. Mirrors icrm-web/src/layouts/pharmacy/billing/JournalLedgerPage.js.

const TEAL = '#00798E';
const TEAL_DARK = '#005f70';
const TEAL_LIGHT = '#e0f4f7';

// First-row-in-tbody header cell — never use <TableHead> in this project.
const HEADER_CELL_SX = {
  fontSize: '0.68rem',
  fontWeight: 700,
  color: 'rgba(255,255,255,0.92)',
  textTransform: 'uppercase',
  letterSpacing: 0.6,
  py: 1.3,
  whiteSpace: 'nowrap',
  borderBottom: 'none',
};

const zebraRowSx = (index) => ({
  background: index % 2 === 0 ? '#fff' : '#fafcff',
  '&:hover': { background: TEAL_LIGHT },
});

// ─── helpers ────────────────────────────────────────────────────────────────

const statusColor = (status) => {
  switch (status) {
    case 'completed':
    case 'sent':
      return 'success';
    case 'failed':
      return 'error';
    case 'partial':
      return 'warning';
    case 'processing':
      return 'info';
    default:
      return 'default';
  }
};

const statusIcon = (status) => {
  switch (status) {
    case 'completed':
    case 'sent':
      return <CheckCircleIcon fontSize="small" />;
    case 'failed':
      return <ErrorIcon fontSize="small" />;
    case 'partial':
      return <HistoryIcon fontSize="small" />;
    default:
      return <PendingIcon fontSize="small" />;
  }
};

const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

// ─── Tab panel ───────────────────────────────────────────────────────────────

function TabPanel({ children, value, index }) {
  return (
    <Box hidden={value !== index} sx={{ pt: 2 }}>
      {value === index && children}
    </Box>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node.isRequired,
  value: PropTypes.number.isRequired,
  index: PropTypes.number.isRequired,
};

// ─── Recipient row inside Send dialog ────────────────────────────────────────

function RecipientRow({ index, recipient, onChange, onRemove }) {
  return (
    <Box
      sx={{
        display: 'flex',
        gap: 1,
        alignItems: 'center',
        mb: 1,
        p: 1,
        borderRadius: 1,
        bgcolor: 'grey.50',
      }}
    >
      <FormControl size="small" sx={{ minWidth: 130 }}>
        <InputLabel>Type</InputLabel>
        <Select
          value={recipient.type}
          label="Type"
          onChange={(e) => onChange(index, 'type', e.target.value)}
        >
          <MenuItem value="mobile">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <PhoneIcon fontSize="small" /> Mobile
            </Box>
          </MenuItem>
          <MenuItem value="external_code">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <PersonAddIcon fontSize="small" /> Patient ID
            </Box>
          </MenuItem>
        </Select>
      </FormControl>

      <TextField
        size="small"
        sx={{ flex: 1 }}
        label={recipient.type === 'mobile' ? '10-digit mobile' : 'Patient external code'}
        value={recipient.value}
        onChange={(e) => onChange(index, 'value', e.target.value)}
        placeholder={recipient.type === 'mobile' ? 'e.g. 9876543210' : 'e.g. IRM-00001'}
      />

      <IconButton size="small" color="error" onClick={() => onRemove(index)}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}

RecipientRow.propTypes = {
  index: PropTypes.number.isRequired,
  recipient: PropTypes.shape({
    type: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
};

// ─── Template variable helper chip ───────────────────────────────────────────

const VARIABLE_CHIPS = [
  '{PATIENT_NAME}',
  '{PATIENT_ID}',
  '{PATIENT_FIRST_NAME}',
  '{PATIENT_LAST_NAME}',
  '{PATIENT_MOBILE}',
  '{PATIENT_EMAIL}',
];

// ═══════════════════════════════════════════════════════════════════════════════
//  Main component
// ═══════════════════════════════════════════════════════════════════════════════

export default function SMS() {
  const [tab, setTab] = useState(0);

  // ── Templates state ────────────────────────────────────────
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);

  // ── Jobs state ─────────────────────────────────────────────
  const [jobs, setJobs] = useState([]);
  const [jobsTotal, setJobsTotal] = useState(0);
  const [jobsPage, setJobsPage] = useState(0);
  const [jobsLimit, setJobsLimit] = useState(10);
  const [jobsLoading, setJobsLoading] = useState(false);

  // ── Log drawer ─────────────────────────────────────────────
  const [expandedJob, setExpandedJob] = useState(null);
  const [jobLogs, setJobLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logDetailOpen, setLogDetailOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);

  // ── Template dialog ────────────────────────────────────────
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({
    name: '',
    template_body: '',
    sender_id: 'IRMKOL',
  });
  const [templateSaving, setTemplateSaving] = useState(false);

  // ── Send SMS dialog ────────────────────────────────────────
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [sendTemplateId, setSendTemplateId] = useState('');
  const [recipients, setRecipients] = useState([{ type: 'mobile', value: '' }]);
  const [sending, setSending] = useState(false);
  const [previewText, setPreviewText] = useState('');

  // ── Load templates ─────────────────────────────────────────
  const loadTemplates = useCallback(async () => {
    setTemplatesLoading(true);
    try {
      const res = await api.communications.getTemplates();
      setTemplates(Array.isArray(res) ? res : res?.data ?? []);
    } catch (e) {
      console.error(e);
    } finally {
      setTemplatesLoading(false);
    }
  }, []);

  // ── Load jobs ──────────────────────────────────────────────
  const loadJobs = useCallback(
    async (page = jobsPage, limit = jobsLimit) => {
      setJobsLoading(true);
      try {
        const res = await api.communications.getJobs(page + 1, limit);
        setJobs(res?.jobs ?? []);
        setJobsTotal(res?.total ?? 0);
      } catch (e) {
        console.error(e);
      } finally {
        setJobsLoading(false);
      }
    },
    [jobsPage, jobsLimit],
  );

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  useEffect(() => {
    if (tab === 1) loadJobs();
  }, [tab, loadJobs]);

  // ── Preview template ───────────────────────────────────────
  useEffect(() => {
    if (!sendTemplateId) {
      setPreviewText('');
      return;
    }
    const t = templates.find((t) => t.id === sendTemplateId);
    setPreviewText(t?.template_body ?? '');
  }, [sendTemplateId, templates]);

  // ─────────────────────────────────────────────────────────────
  //  Template CRUD handlers
  // ─────────────────────────────────────────────────────────────

  const openCreateTemplate = () => {
    setEditingTemplate(null);
    setTemplateForm({ name: '', template_body: '', sender_id: 'IRMKOL' });
    setTemplateDialogOpen(true);
  };

  const openEditTemplate = (t) => {
    setEditingTemplate(t);
    setTemplateForm({
      name: t.name,
      template_body: t.template_body,
      sender_id: t.sender_id,
    });
    setTemplateDialogOpen(true);
  };

  const saveTemplate = async () => {
    if (!templateForm.name.trim() || !templateForm.template_body.trim()) {
      Swal.fire('Validation', 'Name and Template Body are required.', 'warning');
      return;
    }
    setTemplateSaving(true);
    try {
      if (editingTemplate) {
        await api.communications.updateTemplate(editingTemplate.id, templateForm);
        Swal.fire('Updated', 'Template updated successfully.', 'success');
      } else {
        await api.communications.createTemplate(templateForm);
        Swal.fire('Created', 'Template created successfully.', 'success');
      }
      setTemplateDialogOpen(false);
      loadTemplates();
    } catch (e) {
      Swal.fire('Error', e?.response?.data?.message || 'Failed to save template.', 'error');
    } finally {
      setTemplateSaving(false);
    }
  };

  const deleteTemplate = async (t) => {
    const result = await Swal.fire({
      title: 'Delete Template?',
      text: `"${t.name}" will be permanently deleted.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: 'Yes, Delete',
    });
    if (!result.isConfirmed) return;
    try {
      await api.communications.deleteTemplate(t.id);
      Swal.fire('Deleted', 'Template deleted.', 'success');
      loadTemplates();
    } catch (e) {
      Swal.fire('Error', 'Failed to delete template.', 'error');
    }
  };

  const insertVariable = (variable) => {
    setTemplateForm((prev) => ({
      ...prev,
      template_body: prev.template_body + variable,
    }));
  };

  // ─────────────────────────────────────────────────────────────
  //  Send SMS handlers
  // ─────────────────────────────────────────────────────────────

  const openSendDialog = (templateId) => {
    setSendTemplateId(templateId || '');
    setRecipients([{ type: 'mobile', value: '' }]);
    setSendDialogOpen(true);
  };

  const changeRecipient = (index, field, value) => {
    setRecipients((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const addRecipient = () =>
    setRecipients((prev) => [...prev, { type: 'mobile', value: '' }]);

  const removeRecipient = (index) =>
    setRecipients((prev) => prev.filter((_, i) => i !== index));

  const sendSMS = async () => {
    if (!sendTemplateId) {
      Swal.fire('Validation', 'Please select a template.', 'warning');
      return;
    }
    const validRecipients = recipients.filter((r) => r.value.trim());
    if (!validRecipients.length) {
      Swal.fire('Validation', 'Add at least one recipient.', 'warning');
      return;
    }

    const payload = {
      sms_template_id: sendTemplateId,
      recipients: validRecipients.map((r) =>
        r.type === 'mobile'
          ? { mobile: r.value.trim() }
          : { external_code: r.value.trim() },
      ),
    };

    setSending(true);
    try {
      const res = await api.communications.createJob(payload);
      setSendDialogOpen(false);
      Swal.fire({
        icon: 'success',
        title: 'SMS Job Queued!',
        html: `Job <b>#${res?.id}</b> created with <b>${res?.total_count}</b> recipient(s).<br/>Status: <b>${res?.status}</b>`,
      });
      setTab(1);
      setTimeout(() => loadJobs(0, jobsLimit), 1000);
    } catch (e) {
      Swal.fire('Error', e?.response?.data?.message || 'Failed to queue SMS job.', 'error');
    } finally {
      setSending(false);
    }
  };

  // ─────────────────────────────────────────────────────────────
  //  Job logs handlers
  // ─────────────────────────────────────────────────────────────

  const toggleJobLogs = async (jobId) => {
    if (expandedJob === jobId) {
      setExpandedJob(null);
      setJobLogs([]);
      return;
    }
    setExpandedJob(jobId);
    setLogsLoading(true);
    try {
      const res = await api.communications.getJobLogs(jobId, 1, 100);
      setJobLogs(res?.logs ?? []);
    } catch (e) {
      console.error(e);
    } finally {
      setLogsLoading(false);
    }
  };

  const viewLogDetail = async (logId) => {
    try {
      const res = await api.communications.getLogById(logId);
      setSelectedLog(res);
      setLogDetailOpen(true);
    } catch (e) {
      Swal.fire('Error', 'Failed to load log detail.', 'error');
    }
  };

  // ═════════════════════════════════════════════════════════════
  //  Render
  // ═════════════════════════════════════════════════════════════

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={4} pb={3}>
        {/* ── Page Header ─────────────────────────────────────── */}
        <MDBox mb={3} display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: `linear-gradient(135deg, ${TEAL} 0%, ${TEAL_DARK} 100%)`,
                boxShadow: `0 4px 14px ${TEAL}44`,
              }}
            >
              <SmsIcon sx={{ color: '#fff', fontSize: 24 }} />
            </Box>
            <Box>
              <MDTypography variant="h4" fontWeight="bold" color="text">
                SMS Centre
              </MDTypography>
              <MDTypography variant="body2" color="text" opacity={0.7}>
                Manage templates, send bulk SMS and track delivery logs
              </MDTypography>
            </Box>
          </Box>
          <MDButton
            variant="gradient"
            color="info"
            startIcon={<SendIcon />}
            onClick={() => openSendDialog('')}
          >
            Send SMS
          </MDButton>
        </MDBox>

        <Card>
          {/* ── Tabs ──────────────────────────────────────────── */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              TabIndicatorProps={{ sx: { background: TEAL, height: 3, borderRadius: '3px 3px 0 0' } }}
              sx={{ '& .MuiTab-root.Mui-selected': { color: TEAL } }}
            >
              <Tab
                icon={<ArticleIcon />}
                iconPosition="start"
                label="Templates"
                sx={{ minHeight: 48 }}
              />
              <Tab
                icon={<HistoryIcon />}
                iconPosition="start"
                label="Job History"
                sx={{ minHeight: 48 }}
              />
            </Tabs>
          </Box>

          <MDBox p={3}>
            {/* ══════════════════════════════════════════════════
                TAB 0 — Templates
            ══════════════════════════════════════════════════ */}
            <TabPanel value={tab} index={0}>
              <Box display="flex" justifyContent="flex-end" mb={2}>
                <MDButton
                  variant="outlined"
                  color="info"
                  size="small"
                  startIcon={<RefreshIcon />}
                  onClick={loadTemplates}
                  sx={{ mr: 1 }}
                >
                  Refresh
                </MDButton>
                <MDButton
                  variant="gradient"
                  color="success"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={openCreateTemplate}
                >
                  New Template
                </MDButton>
              </Box>

              {templatesLoading ? (
                <Box display="flex" justifyContent="center" py={4}>
                  <CircularProgress sx={{ color: TEAL }} />
                </Box>
              ) : templates.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <ArticleIcon sx={{ fontSize: 52, color: '#e0e0e0', mb: 1.5 }} />
                  <Typography sx={{ fontSize: '0.92rem', fontWeight: 600, color: '#bdbdbd' }}>
                    No templates found
                  </Typography>
                  <Typography sx={{ fontSize: '0.78rem', color: '#cfd8dc', mt: 0.5 }}>
                    Create your first template to start sending SMS.
                  </Typography>
                </Box>
              ) : (
                <Box
                  sx={{
                    borderRadius: 3,
                    border: '1px solid #eef2f5',
                    overflow: 'hidden',
                  }}
                >
                  <Box sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                      <TableBody>
                        {/* Header row lives here, first in TableBody — no <TableHead> per project convention */}
                        <TableRow sx={{ background: `linear-gradient(90deg, ${TEAL} 0%, ${TEAL_DARK} 100%)` }}>
                          <TableCell width={40} sx={HEADER_CELL_SX}>#</TableCell>
                          <TableCell sx={HEADER_CELL_SX}>Name</TableCell>
                          <TableCell sx={HEADER_CELL_SX}>Sender ID</TableCell>
                          <TableCell sx={HEADER_CELL_SX}>Preview</TableCell>
                          <TableCell width={140} align="center" sx={HEADER_CELL_SX}>
                            Actions
                          </TableCell>
                        </TableRow>
                        {templates.map((t, i) => (
                          <TableRow key={t.id} sx={zebraRowSx(i)}>
                            <TableCell sx={{ py: 1.3, fontSize: '0.75rem', color: '#9aa5b1' }}>{i + 1}</TableCell>
                            <TableCell sx={{ py: 1.3 }}>
                              <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#2d3748' }}>
                                {t.name}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ py: 1.3 }}>
                              <Chip
                                label={t.sender_id}
                                size="small"
                                sx={{ fontSize: '0.65rem', fontWeight: 700, height: 20, fontFamily: 'monospace', background: TEAL_LIGHT, color: TEAL_DARK }}
                              />
                            </TableCell>
                            <TableCell sx={{ py: 1.3 }}>
                              <Typography
                                fontSize="0.78rem"
                                color="text.secondary"
                                sx={{
                                  maxWidth: 380,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {t.template_body}
                              </Typography>
                            </TableCell>
                            <TableCell align="center" sx={{ py: 1.3 }}>
                              <Tooltip title="Send this template">
                                <IconButton
                                  size="small"
                                  sx={{ color: '#2e7d32' }}
                                  onClick={() => openSendDialog(t.id)}
                                >
                                  <SendIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Edit">
                                <IconButton
                                  size="small"
                                  sx={{ color: TEAL }}
                                  onClick={() => openEditTemplate(t)}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => deleteTemplate(t)}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                </Box>
              )}
            </TabPanel>

            {/* ══════════════════════════════════════════════════
                TAB 1 — Job History
            ══════════════════════════════════════════════════ */}
            <TabPanel value={tab} index={1}>
              <Box display="flex" justifyContent="flex-end" mb={2}>
                <MDButton
                  variant="outlined"
                  color="info"
                  size="small"
                  startIcon={<RefreshIcon />}
                  onClick={() => loadJobs(jobsPage, jobsLimit)}
                >
                  Refresh
                </MDButton>
              </Box>

              {jobsLoading ? (
                <Box display="flex" justifyContent="center" py={4}>
                  <CircularProgress sx={{ color: TEAL }} />
                </Box>
              ) : jobs.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <InboxIcon sx={{ fontSize: 52, color: '#e0e0e0', mb: 1.5 }} />
                  <Typography sx={{ fontSize: '0.92rem', fontWeight: 600, color: '#bdbdbd' }}>
                    No SMS jobs found yet
                  </Typography>
                </Box>
              ) : (
                <>
                  <Box
                    sx={{
                      borderRadius: 3,
                      border: '1px solid #eef2f5',
                      overflow: 'hidden',
                    }}
                  >
                    <Box sx={{ overflowX: 'auto' }}>
                      <Table size="small">
                        <TableBody>
                          {/* Header row lives here, first in TableBody — no <TableHead> per project convention */}
                          <TableRow sx={{ background: `linear-gradient(90deg, ${TEAL} 0%, ${TEAL_DARK} 100%)` }}>
                            <TableCell width={40} sx={HEADER_CELL_SX}>#</TableCell>
                            <TableCell sx={HEADER_CELL_SX}>Template</TableCell>
                            <TableCell align="center" sx={HEADER_CELL_SX}>Total</TableCell>
                            <TableCell align="center" sx={HEADER_CELL_SX}>Sent</TableCell>
                            <TableCell align="center" sx={HEADER_CELL_SX}>Failed</TableCell>
                            <TableCell sx={HEADER_CELL_SX}>Status</TableCell>
                            <TableCell sx={HEADER_CELL_SX}>Created At</TableCell>
                            <TableCell width={60} align="center" sx={HEADER_CELL_SX}>
                              Logs
                            </TableCell>
                          </TableRow>
                          {jobs.map((job, i) => (
                            <React.Fragment key={job.id}>
                              <TableRow
                                sx={{
                                  ...zebraRowSx(i),
                                  ...(expandedJob === job.id ? { background: TEAL_LIGHT } : {}),
                                }}
                              >
                                <TableCell sx={{ py: 1.3, fontSize: '0.75rem', color: '#9aa5b1' }}>{job.id}</TableCell>
                                <TableCell sx={{ py: 1.3 }}>
                                  <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#2d3748' }}>
                                    {job.template?.name ?? `Template #${job.sms_template_id}`}
                                  </Typography>
                                </TableCell>
                                <TableCell align="center" sx={{ py: 1.3, fontSize: '0.8rem', fontFamily: 'monospace' }}>
                                  {job.total_count}
                                </TableCell>
                                <TableCell align="center" sx={{ py: 1.3 }}>
                                  <Typography sx={{ fontSize: '0.8rem', fontFamily: 'monospace', fontWeight: 700, color: '#2e7d32' }}>
                                    {job.sent_count}
                                  </Typography>
                                </TableCell>
                                <TableCell align="center" sx={{ py: 1.3 }}>
                                  <Typography
                                    sx={{
                                      fontSize: '0.8rem',
                                      fontFamily: 'monospace',
                                      fontWeight: job.failed_count > 0 ? 700 : 400,
                                      color: job.failed_count > 0 ? '#c62828' : '#9aa5b1',
                                    }}
                                  >
                                    {job.failed_count}
                                  </Typography>
                                </TableCell>
                                <TableCell sx={{ py: 1.3 }}>
                                  <Chip
                                    icon={statusIcon(job.status)}
                                    label={job.status.toUpperCase()}
                                    color={statusColor(job.status)}
                                    size="small"
                                    sx={{ fontSize: '0.62rem', fontWeight: 700, height: 22 }}
                                  />
                                </TableCell>
                                <TableCell sx={{ py: 1.3, fontSize: '0.75rem', color: '#546e7a', whiteSpace: 'nowrap' }}>
                                  {fmtDate(job.created_at)}
                                </TableCell>
                                <TableCell align="center" sx={{ py: 1.3 }}>
                                  <Tooltip title="View delivery logs">
                                    <IconButton
                                      size="small"
                                      sx={{ color: TEAL }}
                                      onClick={() => toggleJobLogs(job.id)}
                                    >
                                      {expandedJob === job.id ? (
                                        <ExpandLessIcon fontSize="small" />
                                      ) : (
                                        <ExpandMoreIcon fontSize="small" />
                                      )}
                                    </IconButton>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>

                              {/* ── Expanded Log Rows ───────── */}
                              <TableRow>
                                <TableCell colSpan={8} sx={{ py: 0, border: 0 }}>
                                  <Collapse
                                    in={expandedJob === job.id}
                                    timeout="auto"
                                    unmountOnExit
                                  >
                                    <Box sx={{ mx: 2, my: 1.5, borderRadius: 2, border: '1px solid #eef2f5', overflow: 'hidden' }}>
                                      {logsLoading ? (
                                        <LinearProgress sx={{ '& .MuiLinearProgress-bar': { background: TEAL } }} />
                                      ) : jobLogs.length === 0 ? (
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          sx={{ py: 2, px: 2, display: 'block' }}
                                        >
                                          No logs available yet.
                                        </Typography>
                                      ) : (
                                        <Box sx={{ overflowX: 'auto' }}>
                                          <Table size="small">
                                            <TableBody>
                                              {/* Muted sub-table header — visually one step down from the gradient parent-table header */}
                                              <TableRow sx={{ background: '#f0f4f8' }}>
                                                <TableCell sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#718096', textTransform: 'uppercase', letterSpacing: 0.5 }}>Mobile</TableCell>
                                                <TableCell sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#718096', textTransform: 'uppercase', letterSpacing: 0.5 }}>SMS Text</TableCell>
                                                <TableCell sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#718096', textTransform: 'uppercase', letterSpacing: 0.5 }}>Status</TableCell>
                                                <TableCell sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#718096', textTransform: 'uppercase', letterSpacing: 0.5 }}>HTTP</TableCell>
                                                <TableCell sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#718096', textTransform: 'uppercase', letterSpacing: 0.5 }}>Duration</TableCell>
                                                <TableCell sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#718096', textTransform: 'uppercase', letterSpacing: 0.5 }}>Requested At</TableCell>
                                                <TableCell sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#718096', textTransform: 'uppercase', letterSpacing: 0.5 }}>Responded At</TableCell>
                                                <TableCell width={40} sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#718096' }} />
                                              </TableRow>
                                              {jobLogs.map((log, li) => (
                                                <TableRow key={log.id} sx={zebraRowSx(li)}>
                                                  <TableCell sx={{ py: 1, fontSize: '0.78rem', fontFamily: 'monospace', color: '#2d3748' }}>
                                                    {log.mobile}
                                                  </TableCell>
                                                  <TableCell sx={{ py: 1 }}>
                                                    <Typography
                                                      fontSize="0.75rem"
                                                      color="text.secondary"
                                                      sx={{
                                                        maxWidth: 260,
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                      }}
                                                    >
                                                      {log.sms_text}
                                                    </Typography>
                                                  </TableCell>
                                                  <TableCell sx={{ py: 1 }}>
                                                    <Chip
                                                      icon={statusIcon(log.status)}
                                                      label={log.status}
                                                      color={statusColor(log.status)}
                                                      size="small"
                                                      sx={{ fontSize: '0.6rem', height: 20 }}
                                                    />
                                                  </TableCell>
                                                  <TableCell sx={{ py: 1 }}>
                                                    <Chip
                                                      label={log.http_status ?? '—'}
                                                      size="small"
                                                      variant="outlined"
                                                      sx={{
                                                        fontSize: '0.6rem',
                                                        height: 20,
                                                        fontFamily: 'monospace',
                                                        borderColor:
                                                          log.http_status === 200 || log.http_status === 201
                                                            ? '#a5d6a7'
                                                            : log.http_status >= 400
                                                              ? '#ef9a9a'
                                                              : '#e0e0e0',
                                                        color:
                                                          log.http_status === 200 || log.http_status === 201
                                                            ? '#2e7d32'
                                                            : log.http_status >= 400
                                                              ? '#c62828'
                                                              : '#9aa5b1',
                                                      }}
                                                    />
                                                  </TableCell>
                                                  <TableCell sx={{ py: 1, fontSize: '0.75rem', color: '#546e7a' }}>
                                                    {log.duration_ms !== null && log.duration_ms !== undefined
                                                      ? `${log.duration_ms} ms`
                                                      : '—'}
                                                  </TableCell>
                                                  <TableCell sx={{ py: 1, fontSize: '0.72rem', color: '#78909c' }}>
                                                    {fmtDate(log.requested_at)}
                                                  </TableCell>
                                                  <TableCell sx={{ py: 1, fontSize: '0.72rem', color: '#78909c' }}>
                                                    {fmtDate(log.responded_at)}
                                                  </TableCell>
                                                  <TableCell sx={{ py: 1 }}>
                                                    <Tooltip title="Full detail">
                                                      <IconButton
                                                        size="small"
                                                        sx={{ color: TEAL }}
                                                        onClick={() => viewLogDetail(log.id)}
                                                      >
                                                        <InfoIcon fontSize="small" />
                                                      </IconButton>
                                                    </Tooltip>
                                                  </TableCell>
                                                </TableRow>
                                              ))}
                                            </TableBody>
                                          </Table>
                                        </Box>
                                      )}
                                    </Box>
                                  </Collapse>
                                </TableCell>
                              </TableRow>
                            </React.Fragment>
                          ))}
                        </TableBody>
                      </Table>
                    </Box>
                  </Box>
                  <TablePagination
                    component="div"
                    count={jobsTotal}
                    page={jobsPage}
                    onPageChange={(_, p) => {
                      setJobsPage(p);
                      loadJobs(p, jobsLimit);
                    }}
                    rowsPerPage={jobsLimit}
                    onRowsPerPageChange={(e) => {
                      const l = parseInt(e.target.value, 10);
                      setJobsLimit(l);
                      setJobsPage(0);
                      loadJobs(0, l);
                    }}
                    rowsPerPageOptions={[10, 20, 50]}
                  />
                </>
              )}
            </TabPanel>
          </MDBox>
        </Card>
      </MDBox>

      {/* ══════════════════════════════════════════════════════════
          DIALOG: Create / Edit Template
      ══════════════════════════════════════════════════════════ */}
      <Dialog
        open={templateDialogOpen}
        onClose={() => setTemplateDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingTemplate ? 'Edit Template' : 'New SMS Template'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} sm={8}>
              <TextField
                label="Template Name *"
                fullWidth
                size="small"
                value={templateForm.name}
                onChange={(e) =>
                  setTemplateForm((p) => ({ ...p, name: e.target.value }))
                }
                placeholder="e.g. Appointment Reminder"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Sender ID *"
                fullWidth
                size="small"
                value={templateForm.sender_id}
                onChange={(e) =>
                  setTemplateForm((p) => ({ ...p, sender_id: e.target.value }))
                }
                inputProps={{ maxLength: 11 }}
                helperText="Max 11 chars (DLT approved)"
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                Insert variables:
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                {VARIABLE_CHIPS.map((v) => (
                  <Chip
                    key={v}
                    label={v}
                    size="small"
                    variant="outlined"
                    color="primary"
                    clickable
                    onClick={() => insertVariable(v)}
                  />
                ))}
              </Box>
              <TextField
                label="Template Body *"
                fullWidth
                multiline
                rows={5}
                value={templateForm.template_body}
                onChange={(e) =>
                  setTemplateForm((p) => ({ ...p, template_body: e.target.value }))
                }
                placeholder="Dear {PATIENT_NAME}, your appointment is confirmed. Patient ID: {PATIENT_ID}. - IRM Kolkata"
                helperText={`${templateForm.template_body.length} characters`}
              />
            </Grid>
            {templateForm.template_body && (
              <Grid item xs={12}>
                <Alert severity="info" icon={<VisibilityIcon />}>
                  <Typography variant="caption" fontWeight="600">
                    Preview (with sample values):
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {templateForm.template_body
                      .replace('{PATIENT_NAME}', 'Riya Das')
                      .replace('{PATIENT_ID}', 'IRM-00001')
                      .replace('{PATIENT_FIRST_NAME}', 'Riya')
                      .replace('{PATIENT_LAST_NAME}', 'Das')
                      .replace('{PATIENT_MOBILE}', '9876543210')
                      .replace('{PATIENT_EMAIL}', 'riya@example.com')}
                  </Typography>
                </Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTemplateDialogOpen(false)}>Cancel</Button>
          <MDButton
            variant="gradient"
            color="info"
            onClick={saveTemplate}
            disabled={templateSaving}
            startIcon={templateSaving ? <CircularProgress size={14} /> : null}
          >
            {templateSaving ? 'Saving…' : editingTemplate ? 'Update' : 'Create'}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* ══════════════════════════════════════════════════════════
          DIALOG: Send SMS
      ══════════════════════════════════════════════════════════ */}
      <Dialog
        open={sendDialogOpen}
        onClose={() => setSendDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SendIcon color="success" /> Send SMS
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            {/* Template selector */}
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Select Template *</InputLabel>
                <Select
                  value={sendTemplateId}
                  label="Select Template *"
                  onChange={(e) => setSendTemplateId(e.target.value)}
                >
                  {templates.map((t) => (
                    <MenuItem key={t.id} value={t.id}>
                      {t.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Template preview */}
            {previewText && (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ fontSize: '0.8rem' }}>
                  <Typography variant="caption" fontWeight="600" display="block">
                    Template Body:
                  </Typography>
                  {previewText}
                </Alert>
              </Grid>
            )}

            <Grid item xs={12}>
              <Divider>
                <Typography variant="caption" color="text.secondary">
                  Recipients
                </Typography>
              </Divider>
            </Grid>

            {/* Recipient list */}
            <Grid item xs={12}>
              {recipients.map((r, i) => (
                <RecipientRow
                  key={i}
                  index={i}
                  recipient={r}
                  onChange={changeRecipient}
                  onRemove={removeRecipient}
                />
              ))}
              <MDButton
                variant="outlined"
                color="info"
                size="small"
                startIcon={<AddIcon />}
                onClick={addRecipient}
                sx={{ mt: 1 }}
              >
                Add Recipient
              </MDButton>
            </Grid>

            <Grid item xs={12}>
              <Alert severity="warning" sx={{ fontSize: '0.78rem' }}>
                <strong>Patient ID</strong> will auto-resolve the patient&apos;s name, mobile and
                other details from the database to fill template variables.
              </Alert>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSendDialogOpen(false)}>Cancel</Button>
          <MDButton
            variant="gradient"
            color="success"
            onClick={sendSMS}
            disabled={sending}
            startIcon={sending ? <CircularProgress size={14} /> : <SendIcon />}
          >
            {sending ? 'Queuing…' : `Send to ${recipients.filter((r) => r.value.trim()).length || 0} Recipient(s)`}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* ══════════════════════════════════════════════════════════
          DIALOG: Log Detail
      ══════════════════════════════════════════════════════════ */}
      <Dialog
        open={logDetailOpen}
        onClose={() => setLogDetailOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MessageIcon /> SMS Log Detail #{selectedLog?.id}
        </DialogTitle>
        {selectedLog && (
          <DialogContent dividers>
            <Grid container spacing={1.5}>
              {[
                ['Mobile', selectedLog.mobile],
                ['Status', selectedLog.status?.toUpperCase()],
                ['HTTP Status', selectedLog.http_status ?? '—'],
                ['Duration', selectedLog.duration_ms !== null && selectedLog.duration_ms !== undefined ? `${selectedLog.duration_ms} ms` : '—'],
                ['Requested At', fmtDate(selectedLog.requested_at)],
                ['Responded At', fmtDate(selectedLog.responded_at)],
              ].map(([label, value]) => (
                <Grid item xs={6} key={label}>
                  <Typography variant="caption" color="text.secondary">
                    {label}
                  </Typography>
                  <Typography variant="body2" fontWeight="600">
                    {value}
                  </Typography>
                </Grid>
              ))}

              <Grid item xs={12}>
                <Divider sx={{ my: 0.5 }} />
                <Typography variant="caption" color="text.secondary">
                  SMS Text Sent
                </Typography>
                <Paper
                  variant="outlined"
                  sx={{ p: 1.5, mt: 0.5, bgcolor: 'grey.50', fontSize: '0.82rem' }}
                >
                  {selectedLog.sms_text}
                </Paper>
              </Grid>

              {selectedLog.response_body && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    SMSCountry Response Body
                  </Typography>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      mt: 0.5,
                      bgcolor: 'grey.50',
                      fontSize: '0.75rem',
                      fontFamily: 'monospace',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-all',
                      maxHeight: 200,
                      overflow: 'auto',
                    }}
                  >
                    {(() => {
                      try {
                        return JSON.stringify(JSON.parse(selectedLog.response_body), null, 2);
                      } catch {
                        return selectedLog.response_body;
                      }
                    })()}
                  </Paper>
                </Grid>
              )}

              {selectedLog.error_message && (
                <Grid item xs={12}>
                  <Alert severity="error" sx={{ fontSize: '0.8rem' }}>
                    <strong>Error:</strong> {selectedLog.error_message}
                  </Alert>
                </Grid>
              )}
            </Grid>
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={() => setLogDetailOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}
